import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";
import {
  UploadCloud,
} from "lucide-react";
import Select from "react-select";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import Dropzone from "react-dropzone";
import { Eye } from "lucide-react";
import { Link } from "react-router-dom";
import "flatpickr/dist/flatpickr.css";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../../context/LoaderContext";
import {
  getWarrantyByLoanApplicationId,
  getWarrantyTypeList,
  addWarranty,
  uploadWarrantyFile,
  updateWarranty,
  deleteWarranty,
} from "../../../helpers/fakebackend_helper";
import WarrantyDocument from "./WarrantyDocuments";

const Warranty = forwardRef(
  (
    {
      onSuccess,
      loanApplicationId,
    }: { onSuccess?: () => void; loanApplicationId?: string },
    ref
  ) => {
    const [showWarrantyForm, setShowWarrantyForm] = useState(false);
    const [warrantyList, setWarrantyList] = useState<any[]>([]);
    const [selectfiles, setSelectfiles] = useState<File[]>([]);
    const [warrantyId, setWarratyId] = useState<string | null>(null);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [selectedWarrantyId, setSelectedWarrantyId] = useState<string | null>(null);
    const { showLoader, hideLoader } = useLoader();
    const [warrantyTypeOptions, setWarrantyTypeOptions] = useState<
      { value: number; label: number; code: string }[]
    >([]);

    const handleAddNewWarranty = () => {
      setShowWarrantyForm(true);
    };

    const handleBackToList = () => {
      setShowWarrantyForm(false);
      resetFiles();
    };

    const isHipotecario = () => {
      const currentTypeId = validation.values.warrantyType;
      const selectedOption = warrantyTypeOptions.find(
        (opt) => opt.value === currentTypeId
      );
      return selectedOption?.code?.toUpperCase() === "HIPOTECARIO";
    };

    const isFiduciarioVoucher = () => {
      const currentTypeId = validation.values.warrantyType;
      const selectedOption = warrantyTypeOptions.find(
        (opt) => opt.value === currentTypeId
      );
      return selectedOption?.code?.toUpperCase() === "FDVOUCHER";
    };

    const handleAcceptfiles = (files: any) => {
      const formattedFiles = files.map((file: any) => {
        return Object.assign(file, {
          priview: URL.createObjectURL(file),
          formattedSize: formatBytes(file.size),
        });
      });
      setSelectfiles((prevFiles) => [...prevFiles, ...formattedFiles]);
    };

    const handleFileUpload = async (warrantyId: string) => {
      if (!selectfiles.length) {
        toast.error("No hay archivos seleccionados para subir");
        return;
      }

      showLoader();
      try {
        const formData = new FormData();

        selectfiles.forEach((file: File, index: number) => {
          formData.append(`files`, file);
        });

        formData.append("description", validation.values.comments);

        const response = await uploadWarrantyFile(warrantyId, formData);

        toast.success(response?.data?.message);
        resetFiles();
      } catch (error) {
        toast.error("Error al cargar los archivos");
      } finally {
        hideLoader();
      }
    };

    const formatBytes = (bytes: any, decimals = 2) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const resetFiles = () => {
      setSelectfiles([]);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        validation.handleSubmit();
      },
      resetForm: () => {
        validation.resetForm();
        setWarrantyList([]);
        resetFiles();
      },
    }));

    /**
     * Fetch Warrantys by patrimonialStateId
     */
    const fetchWarrantys = async () => {
      if (!loanApplicationId) return;

      const params = { loanApplicationId };

      showLoader();
      try {
        const response = await getWarrantyByLoanApplicationId(params);
        const warrantys = response.data._embedded.warranties || [];
        setWarrantyList(warrantys);
      } catch (error) {
        toast.error("Error al obtener las garantias"+ error);
      } finally {
        hideLoader();
      }
    };

    useEffect(() => {
      const fetchWarrantyTypes = async () => {
        try {
          const response = await getWarrantyTypeList();
          const embedded = response.data?._embedded?.warrantyTypes;
          const options = embedded
            ? Object.values(embedded).map((warrantyType: any) => ({
                value: warrantyType.warrantyTypeId,
                label: warrantyType.name,
                code: warrantyType.code,
              }))
            : [];
          setWarrantyTypeOptions(options);
        } catch (error) {
          toast.error("Error al obtener los tipos de garantia" + error);
        }
      };
      fetchWarrantyTypes();
    }, []);

    /**
     * Effect to fetch warrantys when patrimonialStateId changes
     */
    useEffect(() => {
      fetchWarrantys();
    }, [loanApplicationId]);

    const linkifyEntity = (entityName: string, id: number) => {
      return `/${entityName}s/${id}`;
    };

    const validation: any = useFormik({
      enableReinitialize: true,
      initialValues: {
        warrantyType: 0,
        estate: "",
        folio: "",
        book: "",
        budgetItem: "",
        ruleNumber: "",
        employeeNumber: "",
        departure: "",
        position: "",
        row: "",
        bank: "",
        accountNumber: "",
        comments: "",
      },
      validationSchema: Yup.object({
        warrantyType: Yup.number()
          .min(1, "Debe seleccionar un tipo de garantía")
          .required("Tipo de garantía es requerido"),
        comments: Yup.string().required("Comentario es requerido"),
      }),
      onSubmit: async (values: any) => {
        if (!loanApplicationId) {
          toast.error("No se ha seleccionado una solicitud de crédito");
          return;
        }

        try {
          const payload = {
            estate: values.estate,
            folio: values.folio,
            book: values.book,
            comments: values.comments,
            budgetItem: values.budgetItem,
            ruleNumber: values.ruleNumber,
            employeeNumber: values.employeeNumber,
            departure: values.departure,
            position: values.position,
            row: values.row,
            bank: values.bank,
            accountNumber: values.accountNumber,
            warrantyType: linkifyEntity(
              "warrantyTypes",
              Number(values.warrantyType)
            ),
            loanApplication: linkifyEntity(
              "loanApplications",
              Number(loanApplicationId)
            ),
          };

          const response = await addWarranty(payload);

          const warrantyId = response.data?.warrantyId;

          if (warrantyId) {
            await handleFileUpload(warrantyId);
          }

          // Refresh the warranty list after adding
          fetchWarrantys();

          setShowWarrantyForm(false);
          validation.resetForm();

          onSuccess?.();
        } catch (error) {
          toast.error("Error al crear la garantia");
        }
      },
    });

    const handleViewDocuments = (warrantyId: string) => {
      setWarratyId(warrantyId);
      setSelectedWarrantyId(warrantyId);
      setShowDocumentModal(true);
    };

    const handleCloseDocumentModal = () => {
      setShowDocumentModal(false);
      setSelectedWarrantyId(null);
    };

    const columns = useMemo(
      () => [
        {
          header: "Tipo de Garantia",
          accessorKey: "warrantyType.name",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Finca",
          accessorKey: "estate",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Folio",
          accessorKey: "folio",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Libro",
          accessorKey: "book",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Partida Presupuestaria",
          accessorKey: "budgetItem",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Número de reglón",
          accessorKey: "ruleNumber",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Número de empleado",
          accessorKey: "employeeNumber",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Partida",
          accessorKey: "departure",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Puesto",
          accessorKey: "position",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Reglón",
          accessorKey: "row",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Entidad bancaria",
          accessorKey: "bank",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Número de cuenta",
          accessorKey: "accountNumber",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Comentarios",
          accessorKey: "comments",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Acciones",
          cell: ({ row }: any) => (
            <div className="flex justify-center">
              <button
                onClick={() => handleViewDocuments(row.original.warrantyId)}
                className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
              >
                <Eye className="inline-block size-3" />
              </button>
            </div>
          ),
        }
      ],
      []
    );

    return (
      <React.Fragment>

        {showDocumentModal && (
          <WarrantyDocument
            warrantyId={selectedWarrantyId ?? undefined}
            onClose={handleCloseDocumentModal}
          />
        )}

        {showWarrantyForm ? (
          <>
            <form onSubmit={validation.handleSubmit}>
              <div className="flex justify-between items-center mt-6">
                <h5 className="mb-3">Agregar Garantia</h5>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleBackToList}
                    className="btn bg-red-500 text-white"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn bg-green-500 text-white">
                    Guardar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Tipo de garantia */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="fromThisAccount"
                    className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                  >
                    Tipo de Garantia <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="warrantyType"
                    value={(() => {
                      const selected = warrantyTypeOptions.find((opt) => {
                        return opt.value === validation.values.warrantyType;
                      });
                      return selected;
                    })()}
                    onChange={(selected) => {
                      if (selected) {
                        validation.setFieldValue(
                          "warrantyType",
                          selected.value
                        );
                      }
                    }}
                    onBlur={validation.handleBlur}
                    options={warrantyTypeOptions}
                    className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
                  />
                  {validation.touched.warrantyType &&
                    validation.errors.warrantyType && (
                      <p className="text-sm text-red-500 mt-1">
                        {validation.errors.warrantyType}
                      </p>
                    )}
                </div>

                {isHipotecario() && (
                  <>
                    {/* Finca */}
                    <div className="col-span-1">
                      <label
                        htmlFor="estate"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Finca
                      </label>
                      <input
                        id="estate"
                        name="estate"
                        value={validation.values.estate}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Folio */}
                    <div className="col-span-1">
                      <label
                        htmlFor="folio"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Folio
                      </label>
                      <input
                        id="folio"
                        name="folio"
                        value={validation.values.folio}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Libro */}
                    <div className="col-span-1">
                      <label
                        htmlFor="book"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Libro
                      </label>
                      <input
                        id="book"
                        name="book"
                        value={validation.values.book}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>
                  </>
                )}

                {isFiduciarioVoucher() && (
                  <>

                    {/* Partida Presupuestaria */}
                    <div className="col-span-1">
                      <label
                        htmlFor="budgetItem"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        No. Partida Presupuestaria
                      </label>
                      <input
                        id="budgetItem"
                        type="number"
                        name="budgetItem"
                        value={validation.values.budgetItem}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Numero de reglón */}
                    <div className="col-span-1">
                      <label
                        htmlFor="ruleNumber"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Número de reglón
                      </label>
                      <input
                        id="ruleNumber"
                        type="number"
                        name="ruleNumber"
                        value={validation.values.ruleNumber}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Numero de empleado */}
                    <div className="col-span-1">
                      <label
                        htmlFor="employeeNumber"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Número de empleado
                      </label>
                      <input
                        id="employeeNumber"
                        type="number"
                        name="employeeNumber"
                        value={validation.values.employeeNumber}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Partida */}
                    <div className="col-span-1">
                      <label
                        htmlFor="departure"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Partida
                      </label>
                      <input
                        id="departure"
                        type="text"
                        name="departure"
                        value={validation.values.departure}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Puesto */}
                    <div className="col-span-1">
                      <label
                        htmlFor="position"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Puesto
                      </label>
                      <input
                        id="position"
                        type="text"
                        name="position"
                        value={validation.values.position}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Reglón */}
                    <div className="col-span-1">
                      <label
                        htmlFor="row"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Reglón
                      </label>
                      <input
                        id="row"
                        type="text"
                        name="row"
                        value={validation.values.row}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Banco */}
                    <div className="col-span-1">
                      <label
                        htmlFor="bank"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Entidad bancaria
                      </label>
                      <input
                        id="bank"
                        type="text"
                        name="bank"
                        value={validation.values.bank}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>

                    {/* Numero de Cuenta */}
                    <div className="col-span-1">
                      <label
                        htmlFor="accountNumber"
                        className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                      >
                        Número de cuenta
                      </label>
                      <input
                        id="accountNumber"
                        type="text"
                        name="accountNumber"
                        value={validation.values.accountNumber}
                        onChange={validation.handleChange}
                        className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                      />
                    </div>
                  </>
                )}

                {/* Comentarios */}
                <div className="col-span-2">
                  <label
                    htmlFor="comments"
                    className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                  >
                    Descripción
                  </label>
                  <input
                    id="comments"
                    name="comments"
                    value={validation.values.comments}
                    onChange={validation.handleChange}
                    className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md"
                  />
                  {validation.touched.comments &&
                    validation.errors.comments && (
                      <p className="text-sm text-red-500 mt-1">
                        {validation.errors.comments}
                      </p>
                    )}
                </div>

                {/* Documentos */}
                <div className="md:col-span-2">
                  <label className="block mb-2 text-base font-medium">
                    Documentos
                  </label>
                  <Dropzone onDrop={handleAcceptfiles}>
                    {({ getRootProps }: any) => (
                      <div className="flex items-center justify-center bg-white border border-dashed rounded-md cursor-pointer border-slate-300 dark:bg-zink-700 dark:border-zink-500">
                        <div
                          className="w-full py-5 text-lg text-center"
                          {...getRootProps()}
                        >
                          <UploadCloud className="mx-auto size-12 text-slate-500 dark:text-zink-200" />
                          <h5 className="text-slate-500 dark:text-zink-200">
                            Drag and drop or <Link to="#">browse</Link>
                          </h5>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                  <ul className="flex flex-wrap mt-4 gap-4">
                    {selectfiles.map((file: File, index: number) => (
                      <li key={index} className="w-64">
                        <div className="border rounded border-slate-200 dark:border-zink-500">
                          <div className="p-2 text-center">
                            <div className="p-2 mx-auto rounded-md size-14 bg-slate-100 dark:bg-zink-600">
                              <img
                                className="block w-full h-full rounded-md"
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                              />
                            </div>
                            <div className="pt-3">
                              <h5
                                className="mb-1 text-sm truncate"
                                title={file.name}
                              >
                                {file.name}
                              </h5>
                              <p className="text-slate-500 dark:text-zink-200 text-sm">
                                {formatBytes(file.size)}
                              </p>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => {
                                  const newFiles = [...selectfiles];
                                  newFiles.splice(index, 1);
                                  setSelectfiles(newFiles);
                                }}
                                className="px-2 py-1.5 text-xs text-white bg-red-500 rounded"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </form>
          </>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Garantias</h2>
              <button
                className="btn bg-blue-500 text-white"
                onClick={handleAddNewWarranty}
              >
                Agregar Garantia
              </button>
            </div>

            {warrantyList.length === 0 ? (
              <p className="text-gray-500">No se han agregado garantias aún.</p>
            ) : (
              <TableContainer
                isPagination={true}
                isGlobalFilter={false}
                columns={columns}
                data={warrantyList}
                customPageSize={5}
                divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                theadclassName="border-b border-slate-200 dark:border-zink-500"
                trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
            )}
          </div>
        )}
      </React.Fragment>
    );
  }
);

export default Warranty;
